import produce from 'immer';
import {
  ACADEMY_CATEGORIES_GET,
  ACADEMY_COURSE_DETAILS_SET_VIEWS,
  ACADEMY_DETAILS_GET,
  ACADEMY_LIST_GET,
  ACADEMY_LO_SET_COMMENT,
  ACADEMY_LO_SET_LIKEDISLIKE,
  ACADEMY_LO_SET_VIEWS,
  ACADEMY_LO_UNSET,
} from '../actions/academy';

const initialState = {
  data: [],
  pager: {},
  categories: [],
  selectedAcademy: {},
  contentNotAvailable: false,
};

const academyReducer = (state = initialState, action) =>
  produce(state, draft => {
    switch (action.type) {
      case ACADEMY_LIST_GET._REQUEST: {
        const { subset } = action;
        if (!subset) {
          draft.data = [];
          draft.pager = {};
        }
        return;
      }

      case ACADEMY_LIST_GET._SUCCESS: {
        const { data, pager, subset } = action;
        const courses =
          (data && Array.isArray(data.courses) && data.courses) ||
          (Array.isArray(data) ? data : []) ||
          [];
        draft.data = subset ? [...state.data, ...courses] : courses;
        draft.pager = pager || {};
        return;
      }

      case ACADEMY_LIST_GET._ERROR: {
        draft.data = [];
        draft.pager = {};
        return;
      }

      case ACADEMY_CATEGORIES_GET._SUCCESS: {
        const { categories = [] } = action;
        draft.categories = Array.isArray(categories)
          ? categories
          : (categories && typeof categories === 'object'
            ? Object.values(categories)
            : []);
        return;
      }

      case ACADEMY_DETAILS_GET._REQUEST: {
        draft.contentNotAvailable = false;
        return;
      }

      case ACADEMY_DETAILS_GET._SUCCESS: {
        const { data: selectedAcademy = {} } = action;
        const { lo_list = [] } = selectedAcademy;

        const mode =
          selectedAcademy.learning_path_mode ||
          selectedAcademy.enforcement ||
          (selectedAcademy.is_mandatory ? 'sequential' : null) ||
          (selectedAcademy.sequential ? 'sequential' : null) ||
          'flat';

        const remapFlat = arr =>
          arr.map((lo, index) => ({
            ...lo,
            index,
            last_item: index === arr.length - 1,
          }));

        const remapSequential = arr =>
          arr.reduce((acc, lo, index) => {
            if (index === 0) {
              return [
                ...acc,
                { ...lo, blocked: false, index, last_item: index === arr.length - 1 },
              ];
            }
            const prev = acc[index - 1];
            const prevDone = prev && (prev.status === 'completed' || prev.status === 'passed');
            return [
              ...acc,
              {
                ...lo,
                blocked: !prevDone,
                index,
                last_item: index === arr.length - 1,
              },
            ];
          }, []);

        draft.selectedAcademy = {
          ...selectedAcademy,
          lo_list: mode === 'sequential' ? remapSequential(lo_list) : remapFlat(lo_list),
        };
        draft.contentNotAvailable = false;
        return;
      }

      case ACADEMY_DETAILS_GET._ERROR: {
        draft.contentNotAvailable = true;
        draft.selectedAcademy = {};
        return;
      }

      case ACADEMY_LO_SET_LIKEDISLIKE._SUCCESS: {
        const { nid, action_type } = action;
        const { selectedAcademy } = state;
        const { lo_list = [] } = selectedAcademy;

        const remappedLOs = lo_list.map(lo_item => {
          if (Number(lo_item.id) !== Number(nid)) return lo_item;

          const likeCount = lo_item.like ?? 0;
          const dislikeCount = lo_item.dislike ?? 0;
          const uaPrev = lo_item.user_actions || {};
          const wasLike = !!uaPrev.like;
          const wasDislike = !!uaPrev.dislike;

          let nextLike = likeCount;
          let nextDislike = dislikeCount;
          let uaNext = { like: false, dislike: false };

          if (action_type === 'like') {
            if (wasLike) {
              nextLike = Math.max(0, likeCount - 1);
            } else if (wasDislike) {
              nextDislike = Math.max(0, dislikeCount - 1);
              nextLike = likeCount + 1;
              uaNext = { like: true, dislike: false };
              return { ...lo_item, like: nextLike, dislike: nextDislike, user_actions: uaNext };
            } else {
              nextLike = likeCount + 1;
              uaNext = { like: true, dislike: false };
            }
          } else if (action_type === 'dislike') {
            if (wasDislike) {
              nextDislike = Math.max(0, dislikeCount - 1);
            } else if (wasLike) {
              nextLike = Math.max(0, likeCount - 1);
              nextDislike = dislikeCount + 1;
              uaNext = { like: false, dislike: true };
              return { ...lo_item, like: nextLike, dislike: nextDislike, user_actions: uaNext };
            } else {
              nextDislike = dislikeCount + 1;
              uaNext = { like: false, dislike: true };
            }
          }

          return { ...lo_item, like: nextLike, dislike: nextDislike, user_actions: uaNext };
        });

        draft.selectedAcademy = {
          ...selectedAcademy,
          lo_list: remappedLOs,
        };
        return;
      }

      case ACADEMY_COURSE_DETAILS_SET_VIEWS._SUCCESS: {
        draft.selectedAcademy = {
          ...state.selectedAcademy,
          user_actions: {
            ...(state.selectedAcademy.user_actions || {}),
            views: true,
          },
        };
        return;
      }

      case ACADEMY_LO_SET_VIEWS._SUCCESS: {
        const { lo_id } = action;
        const { selectedAcademy } = state;
        const { lo_list = [] } = selectedAcademy;

        const remappedLOs = lo_list.map(lo_item => {
          if (Number(lo_item.id) !== Number(lo_id)) return lo_item;
          return {
            ...lo_item,
            user_actions: {
              ...(lo_item.user_actions || {}),
              views: true,
            },
          };
        });

        draft.selectedAcademy = {
          ...selectedAcademy,
          lo_list: remappedLOs,
        };
        return;
      }

      case ACADEMY_LO_SET_COMMENT._SUCCESS: {
        const { payload, lo_id } = action;
        const { selectedAcademy } = state;
        const { lo_list = [] } = selectedAcademy;

        const remappedLOs = lo_list.map(lo_item => {
          if (Number(lo_item.id) !== Number(lo_id)) return lo_item;
          const comments = lo_item.comments || [];
          return { ...lo_item, comments: [payload, ...comments] };
        });

        draft.selectedAcademy = {
          ...selectedAcademy,
          lo_list: remappedLOs,
        };
        return;
      }

      case ACADEMY_LO_UNSET._SUCCESS: {
        draft.selectedAcademy = initialState.selectedAcademy;
        return;
      }

      default:
        return;
    }
  });

export default academyReducer;
